/**
 * ShiftJourney — situational awareness for the nurse's path through the
 * shift-handover journey. Not a checklist that gates: it shows where you
 * are, what's behind you, and how you could proceed — every leg stays
 * optional, order is never enforced (protocols inform, nurses control).
 *
 * Legs and their state derive ENTIRELY from existing reads — the journey is
 * a projection of platform state, never separately stored:
 *   start      ← open shift exists
 *   take over  ← assignments
 *   hand over  ← running handover sessions for patients I still hold
 *   end shift  ← the horizon; the card disappears with the shift
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, MapPin } from 'lucide-react';
import { useActiveShiftAssignments } from '@/hooks/useShifts';
import { useSessions } from '@/hooks/useSessions';
import { HANDOVER_AGENT_SLUG, sessionSurfaceSlug } from '@/lib/sessionRouting';
import { cn } from '@/lib/utils';

type LegState = 'done' | 'current' | 'upcoming';

interface Leg {
  key: string;
  label: string;
  state: LegState;
  hint?: string;
}

export function ShiftJourney() {
  const { t } = useTranslation();

  const { openShift, assignments, patientIds } = useActiveShiftAssignments();
  const activeSet = useMemo(() => new Set(patientIds), [patientIds]);
  const { data: sessions } = useSessions();

  // Outgoing Rapporte in progress: handover sessions for patients I STILL
  // hold (custody not yet transferred), regardless of who opened them.
  const runningRapporte = useMemo(() => {
    const patients = new Set<string>();
    for (const s of sessions ?? []) {
      if (sessionSurfaceSlug(s) !== HANDOVER_AGENT_SLUG) continue;
      if (!s.subject?.id || !activeSet.has(s.subject?.id)) continue;
      if (s.archived_at) continue;
      patients.add(s.subject?.id);
    }
    return patients.size;
  }, [sessions, activeSet]);

  const onShift = !!openShift;
  const hasPatients = assignments.length > 0;

  const legs = useMemo<Leg[]>(() => {
    const takeOverDone = onShift && hasPatients;
    const l: Leg[] = [
      {
        key: 'start',
        label: t('shifts.journey.start', { defaultValue: 'Start your shift' }),
        state: onShift ? 'done' : 'current',
      },
      {
        key: 'takeover',
        label: t('shifts.journey.takeover', { defaultValue: 'Take over patients' }),
        state: !onShift ? 'upcoming' : takeOverDone ? 'done' : 'current',
        hint:
          onShift && hasPatients
            ? t('shifts.journey.patientCount', {
                defaultValue_one: '{{count}} patient in your care',
                defaultValue_other: '{{count}} patients in your care',
                count: assignments.length,
              })
            : undefined,
      },
      {
        key: 'handover',
        label: t('shifts.journey.handover', { defaultValue: 'Hand over' }),
        state: runningRapporte > 0 ? 'current' : 'upcoming',
        hint:
          runningRapporte > 0
            ? t('shifts.journey.rapportRunning', {
                defaultValue_one: 'Rapport running for {{count}} patient',
                defaultValue_other: 'Rapport running for {{count}} patients',
                count: runningRapporte,
              })
            : t('shifts.journey.handoverHint', {
                defaultValue: 'Towards the end of your shift, per patient.',
              }),
      },
      {
        key: 'end',
        label: t('shifts.journey.end', { defaultValue: 'End your shift' }),
        state: 'upcoming',
      },
    ];
    return l;
  }, [t, onShift, hasPatients, assignments.length, runningRapporte]);

  // The first 'current' leg is "you are here".
  const hereIndex = legs.findIndex((l) => l.state === 'current');

  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-4">
      <h2 className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
        {t('shifts.journey.title', { defaultValue: 'Your path through the shift' })}
      </h2>
      <ol className="mt-3">
        {legs.map((leg, i) => {
          const isHere = i === hereIndex;
          return (
            <li key={leg.key} className="relative flex gap-3 pb-4 last:pb-0">
              {/* connector */}
              {i < legs.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute left-[11px] top-6 h-[calc(100%-1.25rem)] w-px',
                    leg.state === 'done' ? 'bg-primary/40' : 'bg-neutral-200',
                  )}
                />
              )}
              {/* marker */}
              <span
                className={cn(
                  'z-10 grid h-6 w-6 flex-none place-items-center rounded-full border',
                  leg.state === 'done'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : isHere
                      ? 'border-primary bg-white text-primary'
                      : 'border-neutral-300 bg-white text-neutral-300',
                )}
              >
                {leg.state === 'done' ? (
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                ) : isHere ? (
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                )}
              </span>
              <div className="min-w-0 flex-1 text-left">
                <span className="flex items-baseline gap-2">
                  <span
                    className={cn(
                      'text-[15px]',
                      leg.state === 'done'
                        ? 'text-neutral-500'
                        : isHere
                          ? 'font-semibold text-neutral-900'
                          : 'text-neutral-400',
                    )}
                  >
                    {leg.label}
                  </span>
                  {isHere && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                      {t('shifts.journey.youAreHere', { defaultValue: 'You are here' })}
                    </span>
                  )}
                </span>
                {leg.hint && (
                  <span className="mt-0.5 flex items-center gap-1 text-[13px] text-neutral-500">
                    {leg.hint}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
